import { classNames } from "@/src/utils/cn";
import { truncateText } from "@/src/utils/truncateText";
import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { usePrefetchDataRoomFolderBrowse } from "@/src/hooks/usePrefetchDataRoomFolderBrowse";

type BreadcrumbFolder = {
  id: string;
  name: string;
};

interface BreadcrumbProps {
  folders: BreadcrumbFolder[];
  isLoading: boolean;
  root: string;
  error?: unknown;
  p?: string;
}

function BreadcrumbLink(props: {
  name: string;
  link: string;
  folderId?: string;
  isLast: boolean;
  isActive: boolean;
}) {
  const prefetchHandlers = usePrefetchDataRoomFolderBrowse(
    props.folderId ?? "",
  );

  return (
    <NavLink
      to={props.link}
      onMouseEnter={props.folderId ? prefetchHandlers.onMouseEnter : undefined}
      onMouseLeave={props.folderId ? prefetchHandlers.onMouseLeave : undefined}
      onFocus={props.folderId ? prefetchHandlers.onFocus : undefined}
    >
      <div className="flex gap-x-1">
        <p
          title={props.name}
          className={classNames(
            "m-0 font-semibold text-sm truncate",
            props.isActive ? "text-persian-500" : "text-gray-500",
          )}
        >
          {truncateText(props.name, 70)}
        </p>
        {!props.isLast ? <p className="text-sm text-gray-400">/</p> : null}
      </div>
    </NavLink>
  );
}

export function Breadcrumbs(props: BreadcrumbProps) {
  const location = useLocation();

  const [parentFolders, setParentFolders] = useState<BreadcrumbFolder[]>([]);

  useEffect(() => {
    if (props.isLoading || props.error) {
      return;
    }

    setParentFolders(props.folders);
  }, [props.folders, props.isLoading, props.error]);

  const crumbs: {
    name: string;
    link: string;
    folderId?: string;
  }[] = [
    {
      name: "Home",
      link: `/${props.root}`,
    },
    ...parentFolders
      .slice(0, parentFolders.length - 1)
      .map((folder) => {
        return {
          name: folder.name,
          link: `/${props.root}/folder/${folder.id}`,
          folderId: folder.id,
        };
      })
      .reverse(),
  ];

  return (
    <div
      className={`flex ${props.p ? props.p : "py-1"} gap-x-1 overflow-y-scroll no-scrollbar max-w-full`}
    >
      {crumbs.map((folder, index) => (
        <BreadcrumbLink
          key={folder.link}
          name={folder.name}
          link={folder.link}
          folderId={folder.folderId}
          isLast={index === crumbs.length - 1}
          isActive={location.pathname === folder.link}
        />
      ))}
    </div>
  );
}
